import { useEffect, useMemo, useRef, useState } from 'react'

import {
  clockTime,
  dayKey,
  dayOfMonth,
  hourIn,
  monthLabel,
  relativeDayName,
  weekdayShort,
} from '../lib/datetime'
import type { Slot } from '../lib/types'

/**
 * Pick a day, then a time — the shape every booking product uses, because it is the shape people
 * already know.
 *
 * What it replaces: a vertical stack of every open day with every open time under it. That is
 * correct and complete and it was five phone screens of identical buttons, with no sense of
 * which days were busy and no way to reach next Tuesday except by scrolling past this one.
 *
 * Two things carry the design:
 *
 *  * **A day rail.** Horizontally scrollable on a phone, a paged week on a laptop, and the same
 *    component in both — one set of behaviour to get right. Each day states how many times are
 *    open, so a day worth opening is visible before it is opened.
 *  * **Parts of the day.** Morning, afternoon and evening, because that is how somebody decides:
 *    they know they want an afternoon before they know they want 2:30.
 *
 * Everything is driven by the `slots` the API returned. There is no client-side scheduling — a
 * day is bookable here if and only if the server offered a slot on it.
 */

interface Props {
  slots: Slot[]
  timezone: string
  disabled?: boolean
  onPick: (slot: Slot) => void
  /** The slot currently being booked, so only that button shows the pending state. */
  pendingStart?: string | null
}

interface Day {
  key: string
  first: string
  slots: Slot[]
}

const PARTS = [
  { id: 'morning', label: 'Morning', until: 12 },
  { id: 'afternoon', label: 'Afternoon', until: 17 },
  { id: 'evening', label: 'Evening', until: 24 },
] as const

/** How many days the rail shows at once on a laptop. A week reads as a week. */
const WEEK = 7

export function SlotPicker({ slots, timezone, disabled, onPick, pendingStart }: Props) {
  const days = useMemo<Day[]>(() => {
    const groups = new Map<string, Slot[]>()
    for (const slot of slots) {
      const key = dayKey(slot.start, timezone)
      groups.set(key, [...(groups.get(key) ?? []), slot])
    }
    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, daySlots]) => ({ key, first: daySlots[0]!.start, slots: daySlots }))
  }, [slots, timezone])

  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const railRef = useRef<HTMLDivElement>(null)

  // Default to the soonest day with anything open, and recover if that day is booked out from
  // under us — the list refetches every minute and after a lost race.
  useEffect(() => {
    const first = days[0]
    if (!first) return
    if (!selectedKey || !days.some((day) => day.key === selectedKey)) {
      setSelectedKey(first.key)
      setPage(0)
    }
  }, [days, selectedKey])

  // Every read below assumes at least one day, so bail before any of them.
  if (days.length === 0) return null

  const selected = days.find((day) => day.key === selectedKey) ?? days[0]!
  const pageCount = Math.max(1, Math.ceil(days.length / WEEK))
  const pageDays = days.slice(page * WEEK, page * WEEK + WEEK)
  const railDays = pageDays.length > 0 ? pageDays : days

  function choose(day: Day) {
    setSelectedKey(day.key)
    // Bring the chosen day into view on the phone rail, where it may be half off-screen.
    const el = railRef.current?.querySelector<HTMLElement>(`[data-day="${day.key}"]`)
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }

  const byPart = PARTS.map((part, index) => ({
    ...part,
    slots: selected.slots.filter((slot) => {
      const hour = hourIn(slot.start, timezone)
      const from = index === 0 ? 0 : PARTS[index - 1]!.until
      return hour >= from && hour < part.until
    }),
  })).filter((part) => part.slots.length > 0)

  return (
    <div className="mt-8">
      {/* ---------------------------------------------------------------- day rail */}
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-lg">{monthLabel(railDays[0]!.first, timezone)}</h2>
        {/* Paging is a laptop affordance; a phone scrolls the rail with a thumb. */}
        <div className="hidden gap-1.5 sm:flex">
          <RailButton
            label="Earlier days"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <Chevron direction="left" />
          </RailButton>
          <RailButton
            label="Later days"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            <Chevron direction="right" />
          </RailButton>
        </div>
      </div>

      <div
        ref={railRef}
        role="group"
        aria-label="Pick a day"
        className="-mx-5 mt-3 flex snap-x snap-mandatory gap-2 overflow-x-auto px-5 pb-2 sm:mx-0 sm:grid sm:snap-none sm:grid-cols-7 sm:overflow-visible sm:px-0 sm:pb-0"
      >
        {railDays.map((day) => {
          const on = day.key === selected.key
          const relative = relativeDayName(day.first, timezone)
          return (
            <button
              key={day.key}
              data-day={day.key}
              type="button"
              aria-pressed={on}
              onClick={() => choose(day)}
              className={`min-h-[76px] w-[84px] shrink-0 snap-start rounded-card border px-2 py-2.5 text-center transition-colors sm:w-auto ${
                on
                  ? 'border-navy bg-navy text-white'
                  : 'border-line bg-white text-ink hover:border-blue hover:bg-blue-tint'
              }`}
            >
              <span
                className={`block font-mono text-xs uppercase tracking-[0.1em] ${
                  on ? 'text-white/70' : 'text-muted'
                }`}
              >
                {relative || weekdayShort(day.first, timezone)}
              </span>
              <span className="mt-0.5 block font-display text-xl leading-none">
                {dayOfMonth(day.first, timezone)}
              </span>
              <span className={`mt-1 block text-xs ${on ? 'text-white/70' : 'text-muted'}`}>
                {day.slots.length} {day.slots.length === 1 ? 'time' : 'times'}
              </span>
            </button>
          )
        })}
      </div>

      {/* ---------------------------------------------------------------- the times */}
      <div className="mt-7">
        <h3 className="sr-only">
          Times on {weekdayShort(selected.first, timezone)} {dayOfMonth(selected.first, timezone)}
        </h3>
        <div className="grid gap-6">
          {byPart.map((part) => (
            <section key={part.id} aria-labelledby={`part-${part.id}`}>
              <p
                id={`part-${part.id}`}
                className="m-0 font-mono text-xs uppercase tracking-[0.14em] text-muted"
              >
                {part.label}
              </p>
              {/* Two columns only on the very narrowest phones; three fits "10:00 AM" from
                  360 px up, and a three-across grid of times reads as a set of choices rather
                  than a stack of buttons. */}
              <ul className="m-0 mt-2.5 grid list-none grid-cols-2 gap-2 p-0 min-[360px]:grid-cols-3 sm:grid-cols-4 lg:grid-cols-5">
                {part.slots.map((slot) => {
                  const pending = pendingStart === slot.start
                  return (
                    <li key={slot.start}>
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onPick(slot)}
                        className="min-h-[48px] w-full rounded-btn border border-line-2 bg-white px-2 font-semibold text-ink transition-colors hover:border-blue hover:bg-blue-tint disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {pending ? 'Booking…' : clockTime(slot.start, timezone)}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  )
}

function RailButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-11 place-items-center rounded-btn border border-line bg-white text-ink transition-colors hover:border-blue hover:bg-blue-tint disabled:opacity-35 disabled:hover:border-line disabled:hover:bg-white"
    >
      {children}
    </button>
  )
}

function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d={direction === 'left' ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'}
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
